import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UseFormReturn } from 'react-hook-form';
import { TFunction } from 'react-i18next';

import { postToast } from 'features/common/utils/postFlash';

import { checkTicketsAvailability, invalidateAfterSend, sendTickets } from '../services';
import { CompIssued, FormData, NestedTicketObject } from '../types';

interface UseAvailabilityAndSendOptions {
	formMethods: UseFormReturn<FormData>;
	t: TFunction;
	/** Event id — used to invalidate this event's capacity sources after a successful single/seated send. */
	id: string;
	refetch: () => void;
	onSuccess: (issued: CompIssued[]) => void;
}

export const useAvailabilityAndSend = ({ formMethods, t, id, refetch, onSuccess }: UseAvailabilityAndSendOptions) => {
	const queryClient = useQueryClient();
	const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
	// One flag for the whole chain: react-query clears the first mutation before starting the second,
	// from a promise callback outside React 17's batching, so an OR of their isLoadings flickers.
	const [isSending, setIsSending] = useState(false);
	// A ref, not the state above: setIsSending does not take effect until the next render, so a second
	// submit dispatched in the same tick (a double click, or Enter while the first is pending) would
	// read the stale `false` and start a second chain — two comps for one intent.
	const inFlightRef = useRef(false);

	const settle = () => {
		inFlightRef.current = false;
		setIsSending(false);
	};

	const { mutate: mutateSendTickets } = useMutation({
		mutationFn: sendTickets,
		onSuccess: (issued) => {
			invalidateAfterSend(queryClient, id);
			postToast('success', t('send-tickets-success'));
			settle();
			onSuccess(issued);
		},
		onError: () => {
			settle();
			postToast('error', t('send-tickets-error'));
		},
	});

	const { mutate: mutateCheckAvailability } = useMutation({
		mutationFn: (tickets: NestedTicketObject) => checkTicketsAvailability(tickets),
		onSuccess: () => mutateSendTickets(formMethods.getValues()),
		onError: () => {
			settle();
			setIsErrorModalOpen(true);
		},
	});

	const startSend = (tickets: NestedTicketObject) => {
		if (inFlightRef.current) return;
		inFlightRef.current = true;
		setIsSending(true);
		mutateCheckAvailability(tickets);
	};

	const closeErrorModal = () => {
		setIsErrorModalOpen(false);
		refetch();
	};

	return { mutateCheckAvailability: startSend, isSending, isErrorModalOpen, closeErrorModal };
};
